// Merkan · Dateien teilen oder herunterladen
//
// Smartphone und Tablet: natives Teilen-Menü des Systems (Web Share API, z. B. Mail, WhatsApp, Dateien, AirDrop).
// PC: Herunterladen, auch wenn der Browser teilen könnte, damit die Datei wie gewohnt im Download-Ordner landet.
//
// Browser erlauben das Teilen nur unmittelbar nach einem Tipp. Dauert das Erzeugen der Datei zu lange
// (vor allem iOS Safari), meldet teilenJetzt() 'freigabe'; dann einen zweiten Tipp anbieten (siehe ui/teilen.tsx).

export interface TeilDatei {
  dateiname: string;
  typ: string;
  daten: Blob;
}

/** Ergebnis: geteilt, im Teilen-Menü abgebrochen, heruntergeladen oder ein erneuter Tipp ist nötig */
export type TeilErgebnis = 'geteilt' | 'abgebrochen' | 'heruntergeladen' | 'freigabe';

/** Base64 (ohne Data-URL-Präfix) als Blob */
export function base64Blob(b64: string, typ: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: typ });
}

/** Smartphone oder Tablet mit Touch-Bedienung (iPadOS meldet sich als Mac, erkennbar an den Touchpunkten) */
export function mobilesGeraet(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const grob = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  const ua = navigator.userAgent;
  const mobil = /Android|iPhone|iPad|iPod|Mobile/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  return grob && mobil;
}

const alsDatei = (d: TeilDatei) => new File([d.daten], d.dateiname, { type: d.typ });

/** Ob die Datei über das native Teilen-Menü gehen soll (nur mobil und nur, wenn der Browser Dateien teilen kann) */
export function kannTeilen(d: TeilDatei): boolean {
  if (!mobilesGeraet() || typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return false;
  try {
    return navigator.canShare({ files: [alsDatei(d)] });
  } catch {
    return false;
  }
}

/** Datei herunterladen (Objekt-URL statt Data-URL, damit auch große PDFs und iOS funktionieren) */
export function herunterladen(d: TeilDatei): void {
  const url = URL.createObjectURL(d.daten);
  const a = document.createElement('a');
  a.href = url;
  a.download = d.dateiname;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Öffnet das Teilen-Menü sofort. Muss direkt aus einem Tipp heraus laufen. */
export async function teilenJetzt(d: TeilDatei, titel?: string): Promise<TeilErgebnis> {
  try {
    await navigator.share({ files: [alsDatei(d)], title: titel ?? d.dateiname });
    return 'geteilt';
  } catch (e) {
    const name = (e as DOMException | null)?.name;
    if (name === 'AbortError') return 'abgebrochen';
    if (name === 'NotAllowedError') return 'freigabe';
    herunterladen(d);
    return 'heruntergeladen';
  }
}

/** Mobil teilen, am PC herunterladen */
export async function teilenOderLaden(d: TeilDatei, titel?: string): Promise<TeilErgebnis> {
  if (!kannTeilen(d)) {
    herunterladen(d);
    return 'heruntergeladen';
  }
  return teilenJetzt(d, titel);
}
